/**
 * useFeed Hook
 * Manages social feed with pagination and infinite scroll
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { socialService } from '../../services/api/socialService';
import { FeedItem } from '../../types/social.types';

interface UseFeedOptions {
  userId?: string;
  limit?: number;
  autoFetch?: boolean;
}

interface UseFeedReturn {
  items: FeedItem[];
  loading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const useFeed = (options: UseFeedOptions = {}): UseFeedReturn => {
  const { userId, limit = 20, autoFetch = true } = options;

  const [items, setItems] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const isLoadingRef = useRef(false);

  const fetchFeed = useCallback(
    async (pageNum: number, reset: boolean = false) => {
      if (isLoadingRef.current) return;

      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const data = userId
          ? await socialService.getUserActivity(userId, {
              page: pageNum,
              limit
            })
          : await socialService.getUserFeed({ page: pageNum, limit });

        if (reset) {
          setItems(data.activities ?? []);
        } else {
          setItems((prev) => [...prev, ...(data.activities ?? [])]);
        }

        setHasMore((data.activities ?? []).length === limit);
        setPage(pageNum);
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Failed to fetch feed')
        );
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    },
    [userId, limit]
  );

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchFeed(page + 1, false);
  }, [hasMore, loading, page, fetchFeed]);

  const refresh = useCallback(async () => {
    setItems([]);
    setHasMore(true);
    await fetchFeed(1, true);
  }, [fetchFeed]);

  useEffect(() => {
    if (autoFetch) {
      fetchFeed(1, true);
    }
  }, [autoFetch, fetchFeed]);

  return {
    items,
    loading,
    error,
    hasMore,
    loadMore,
    refresh
  };
};
